/**
 * IonRemainingChip (2026-09-08; inverted to a consumption pill 2026-09-15)
 * ==================================================================
 * The always-visible allowance pill in the Ion header: a small bar that FILLS
 * as Insights tokens are spent, with "n/20 today" beside it. (Shipped
 * 2026-09-08 as a number-only pill; the bar was requested the next day — a
 * fraction reads at a glance, a bare number needs the cap to mean anything.
 * It drained from full to empty until 2026-09-15, when it was turned around to
 * fill from zero: same direction as IonUsageMeter's bars, so the two surfaces
 * no longer move opposite ways for the same event, and the cap is now on the
 * pill itself rather than only in the tooltip.)
 *
 * NAMING, since it now reads backwards: the component, its CSS classes
 * (`.ion-remaining-chip*`), its `data-testid` and the `ion.remaining.*` i18n
 * keys all still say "remaining". They are the 2026-09-08 names and were left
 * alone deliberately — renaming them touches ion.css, ChatPanelHeader, the
 * test ids and every key, which is a rename commit, not this one. What the
 * chip SHOWS is consumption: `windowState` returns `used`/`usedPct`, and there
 * is no remaining value in this file any more.
 *
 * `IonUsageMeter` deliberately stays hidden until a budget window is 80%
 * consumed (its meters are the *detail* view that surfaces near the cap).
 * Customers asked where they stand before that point, so this chip is the
 * persistent *glance* value: the tightest finite window, updated
 * live from the same signals the meter reads (`insightsTokenBudget` /
 * `weeklyTokenBudget` / `monthlyTokenBudget`, seeded by
 * `ionAccessService.bootstrapChatAccess` from GET /chat/usage/me and moved
 * mid-chat by the turn-end `quota` stream event).
 *
 * Display unit is the Insights Token (see backend `chat/token_units.py`) —
 * never USD on a customer surface (DEC-6).
 *
 * Window choice (changed 2026-09-16): the chip shows the **binding** window —
 * the capped one with the **fewest Insights tokens left**, in absolute tokens
 * — not the first of daily → weekly → monthly that happens to have a cap. The
 * old order meant a PRO user at 2/20 today and 199/200 this month read
 * "2/20 today": the one window that was not about to stop them. Ties keep the
 * old order.
 *
 * "Fewest left" is measured in TOKENS, not in percent consumed. Those two
 * disagree the moment the caps differ in size — 5/20 today (15 left) against
 * 150/200 this month (50 left) binds on TODAY, even though the month is 75%
 * spent and the day only 25%. See the comment at the `tokensLeft` reduce.
 *
 * ONE CONSEQUENCE WORTH KNOWING: the pill's orange (`primary.state.near`) is
 * still a PERCENTAGE judgement — `warning` from the server, or `nearPct >= 80`
 * — now applied to a window chosen on absolute remainder. The two scales can
 * disagree: 5/20 today (15 left, 25%, calm) against 180/200 this month
 * (20 left, 90%, near) selects today and draws a CALM pill, while the card
 * below it draws an orange bar for the month and `IonUsageMeter` is visible.
 * That is the intended reading — 15 tokens is what stops you first — but the
 * pairing is new and looks like a bug if you are not expecting it.
 *
 * THE BREAKDOWN IS A HOVER CARD, NOT A `title` (changed 2026-09-16). The
 * `title` attribute handed the breakdown to the browser's own tooltip:
 * unstyled, a second late, drawn in OS chrome rather than in the product, its
 * line breaks honoured on some browsers and not others, and with nowhere to
 * put a per-window bar. It is now the same portalled card the rest of Ion
 * uses (see `DataValueChip`) — `position: fixed` on `document.body`, so no
 * ancestor `overflow: hidden` can clip it — but positioned AT THE CURSOR and
 * following it while the pointer is over the chip, flipping to the other side
 * of the cursor rather than overflowing a viewport edge.
 *
 * The card lists EVERY window the profile has, unlimited ones included, and
 * marks the binding one. Unlimited windows used to be filtered out, which made
 * the chip silent about them on `batemo` (daily and weekly unlimited by
 * default, backend `quota.py`) and on any per-user override that lifts a cap.
 *
 * Renders null when the profile has no Ion budget (FREE / no subscription /
 * unknown) or when NO window has a cap, so it costs nothing there.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import {
  insightsTokenBudget,
  weeklyTokenBudget,
  monthlyTokenBudget,
  tierProfile,
} from '../../state/chatStore.js'
import { t } from '../../i18n/index.js'

// Gap between the cursor and the card's near corner, and the margin the card
// keeps from the viewport edge. The vertical gap clears a typical cursor
// hotspot so the card never sits under the arrow itself.
const CURSOR_GAP_X = 14
const CURSOR_GAP_Y = 18
const VIEWPORT_PAD = 8

function isTieredProfile() {
  const p = tierProfile.value
  return p === 'pro' || p === 'demo' || p === 'batemo'
}

function fmtAmount(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

/**
 * Normalise one budget signal into { used, usedPct, limit, near } or null when
 * the window is unknown/unlimited/invalid.
 *
 * `used` is clamped into 0..limit and rounded to the tenth the label prints:
 * a turn that overshot the cap reads "20/20", never "25/20" (the mirror of the
 * old "never a negative allowance" rule), and a negative or junk `used` reads 0.
 *
 * `usedPct` (0–100, integer) drives the bar, which now GROWS with consumption
 * — same direction as IonUsageMeter's fills. It is derived from the displayed
 * `used` so bar and label can never contradict each other, and it is pinned at
 * the ends rather than merely rounded:
 *   - a DISPLAYED zero → exactly 0, so an empty bar always matches an empty
 *     label. Note the pin is on the displayed figure, not on the raw one:
 *     0.04 spent prints "0/20" and draws 0%. That is deliberate and is the
 *     exact mirror of the old rule, which pinned "0 left" to an empty bar from
 *     19.96 upwards.
 *   - anything spent, cap not reached → 1..99, so a first sip is visible
 *     (0.1/20 → 1%, not 0%) and a nearly-exhausted window still reads as not
 *     quite full (19.9/20 → 99%, not 100% — the inverse of the old sliver rule)
 *   - at or over the cap → exactly 100
 */
export function windowState(budget) {
  if (!budget || budget.limit == null) return null
  const limit = Number(budget.limit)
  if (!Number.isFinite(limit) || limit <= 0) return null
  const rawUsed = Math.max(0, Number(budget.used) || 0)
  const used = Math.min(limit, Math.round(rawUsed * 10) / 10)
  // Feeds `near` ONLY, and is raw on purpose: it keeps the 80% threshold
  // bit-identical to the pre-inversion behaviour (and to IonUsageMeter's
  // `isWindowNear`). Deriving it from the rounded, clamped `used` would quietly
  // move when the pill turns orange — do not "tidy" it into `usedPct`.
  const nearPct = Math.min(100, Math.round((Math.min(rawUsed, limit) / limit) * 100))
  const usedPct =
    used <= 0
      ? 0
      : used >= limit
        ? 100
        : Math.min(99, Math.max(1, Math.round((used / limit) * 100)))
  return { used, usedPct, limit, near: Boolean(budget.warning) || nearPct >= 80 }
}

/**
 * True when the profile HAS this window and it carries no cap.
 *
 * `limit: null` alone cannot answer this: it is also the value of every budget
 * signal before `bootstrapChatAccess` has answered, so reading it as
 * "unlimited" would announce three unlimited windows to every user for the
 * first moments of a session. `used` is what separates the two — the bootstrap
 * writes a decimal STRING there (possibly "0"), and nothing else does. Hence
 * the explicit null check before `Number()`, which would otherwise turn a null
 * `used` into a perfectly finite 0.
 */
function isUnlimited(budget) {
  if (!budget || budget.limit != null) return false
  if (budget.used == null) return false
  return Number.isFinite(Number(budget.used))
}

/**
 * The cursor-bound breakdown card.
 *
 * `cursor` is the live pointer position in VIEWPORT coordinates (clientX/Y),
 * which is exactly what `position: fixed` wants: no scroll offset enters the
 * arithmetic, and a scroll moves neither the cursor nor the card, so the pair
 * simply does not care about one (see the listener comment below, which is
 * where that stops being a nicety).
 *
 * Placement is measured, not guessed: the card paints `visibility: hidden` on
 * its first frame, `useLayoutEffect` reads its real box before paint, and only
 * then is it placed and shown. Below-right of the cursor by default; it flips
 * to the other side of the cursor on whichever axis would overflow, and clamps
 * to the viewport as a last resort when neither side fits.
 *
 * `aria-hidden`: the chip's own `aria-label` already carries every window as
 * one sentence, and the chip is an aria-live region — exposing the same facts
 * again would have a screen reader read the whole breakdown a second time on
 * every turn-end quota tick. This card is the POINTER surface only.
 */
function BreakdownCard({ cursor, windows }) {
  const cardRef = useRef(null)
  const [placement, setPlacement] = useState({ top: 0, left: 0, visibility: 'hidden' })

  // Re-placed when the cursor moves AND when the card's own height changes
  // under a stationary pointer: a turn-end quota tick can add or remove a bar
  // or the binding note, and a card that had flipped above the cursor would
  // otherwise grow back past the edge it flipped away from until the next
  // mousemove. Row count plus which row is binding is the whole of what can
  // change the box — the values themselves are one line either way.
  const shape = `${windows.length}:${windows.map((w) => (w.state ? 'c' : 'u')).join('')}:${windows
    .map((w) => (w.binding ? '1' : '0'))
    .join('')}`

  useLayoutEffect(() => {
    const card = cardRef.current
    if (!card) return
    const box = card.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    let left = cursor.x + CURSOR_GAP_X
    if (left + box.width > vw - VIEWPORT_PAD) left = cursor.x - CURSOR_GAP_X - box.width
    if (left < VIEWPORT_PAD) left = VIEWPORT_PAD

    let top = cursor.y + CURSOR_GAP_Y
    if (top + box.height > vh - VIEWPORT_PAD) top = cursor.y - CURSOR_GAP_Y - box.height
    if (top < VIEWPORT_PAD) top = VIEWPORT_PAD

    setPlacement({ top, left, visibility: 'visible' })
  }, [cursor.x, cursor.y, shape])

  return (
    <div
      ref={cardRef}
      class="ion-remaining-card"
      aria-hidden="true"
      data-testid="ion-remaining-card"
      style={{
        position: 'fixed',
        top: `${placement.top}px`,
        left: `${placement.left}px`,
        visibility: placement.visibility,
      }}
    >
      <div class="ion-remaining-card__heading">{t('ion.remaining.tooltip.heading')}</div>
      <ul class="ion-remaining-card__rows">
        {windows.map((w) => (
          <li
            key={w.key}
            class={`ion-remaining-card__row${w.binding ? ' ion-remaining-card__row--binding' : ''}`}
            data-window={w.key}
          >
            <span class="ion-remaining-card__line">
              <span class="ion-remaining-card__window">{w.name}</span>
              <span class="ion-remaining-card__value">
                {w.state
                  ? t('ion.remaining.tooltip.value', {
                      n: fmtAmount(w.state.used),
                      limit: fmtAmount(w.state.limit),
                    })
                  : t('ion.remaining.tooltip.unlimited')}
              </span>
            </span>
            {/* An unlimited window gets no bar: there is no cap to draw
                against, and both a full and an empty track would read as a
                figure the window does not have. */}
            {w.state && (
              <span class="ion-remaining-card__track">
                <span
                  class={`ion-remaining-card__fill${w.state.near ? ' ion-remaining-card__fill--warn' : ''}`}
                  style={{ width: `${w.state.usedPct}%` }}
                />
              </span>
            )}
            {w.binding && (
              <span class="ion-remaining-card__note">{t('ion.remaining.tooltip.binding')}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function IonRemainingChip() {
  // `cursor` doubles as the open flag: null is closed, {x, y} is open at that
  // viewport point. One piece of state, so the card can never be open without
  // a position to render at.
  const [cursor, setCursor] = useState(null)

  // DELIBERATELY NOT CLOSED ON SCROLL. The obvious thing — and what
  // DataValueChip does — is a capturing `scroll` listener, but that tooltip is
  // anchored to an ELEMENT, so a scroll genuinely strands it. This card is
  // anchored to a VIEWPORT point on a `position: fixed` element: a scroll
  // moves neither the cursor nor the card, so there is nothing to invalidate.
  // Closing on one would be actively wrong here, because Ion scrolls its own
  // message list programmatically on every text-delta (ChatMessageList's
  // scrollTop effect, keyed on a `messages` array that updateMessage replaces
  // per delta) — the card would dismiss itself under a stationary pointer for
  // the whole of a streaming reply, and again on the turn-end quota tick, i.e.
  // exactly when the allowance is worth reading.
  //
  // What does close it, besides the pointer leaving: a resize (the clamp was
  // computed against a viewport that no longer exists), Escape (WCAG 1.4.13
  // wants custom hover content dismissible — the `title` this replaces was
  // UA-controlled and exempt; this is not), and the next touch (a tap
  // synthesises mouseenter/mousemove but not a reliable mouseleave, so without
  // this a touch user would be left with a card stuck to the screen).
  //
  // Keyed on the OPEN flag, not on the coordinates, so a mousemove does not
  // re-bind the listeners on every frame.
  const isOpen = Boolean(cursor)
  useEffect(() => {
    if (!isOpen) return undefined
    const close = () => setCursor(null)
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('resize', close)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('touchstart', close, true)
    return () => {
      window.removeEventListener('resize', close)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('touchstart', close, true)
    }
  }, [isOpen])

  if (!isTieredProfile()) return null

  const monthly = monthlyTokenBudget.value
  const all = [
    { key: 'today', budget: insightsTokenBudget.value },
    { key: 'week', budget: weeklyTokenBudget.value },
    { key: monthly?.resetAt === 'never' ? 'demo' : 'month', budget: monthly },
  ]
    .map((w) => ({ ...w, state: windowState(w.budget), unlimited: isUnlimited(w.budget) }))
    // Drop only the windows we know NOTHING about (not yet bootstrapped).
    // An unlimited window is a fact worth telling — see the card below.
    .filter((w) => w.state || w.unlimited)

  const capped = all.filter((w) => w.state)
  if (capped.length === 0) return null

  // THE CHIP SHOWS THE BINDING WINDOW, not the shortest one (changed
  // 2026-09-16). It used to take the first of daily → weekly → monthly that
  // had a cap, so a PRO user sitting at 2/20 today but 199/200 this month read
  // "2/20 today" — a comfortable number, from the one window that was not
  // about to stop them.
  //
  // BINDING IS THE FEWEST TOKENS LEFT, IN ABSOLUTE TOKENS — not the largest
  // share consumed (corrected 2026-09-16, same day: the first version of this
  // compared `used / limit`). The two disagree whenever the caps differ in
  // size, and the percentage answer is the wrong one:
  //
  //     today  5/20   → 15 left, 25% spent
  //     month  150/200 → 50 left, 75% spent
  //
  // By share, the month "binds" at 75%. But the user can spend 15 more tokens
  // before anything stops them, and that number comes from TODAY. A percentage
  // describes how far through a window you are; only the absolute remainder
  // says how much you may still spend, which is the question the glance value
  // exists to answer. The smallest remainder is by definition the first cap you
  // will hit.
  //
  // `used` is already clamped into 0..limit by `windowState`, so a remainder is
  // never negative, and an overshot window reads 0 — correctly binding. Ties
  // keep the daily → weekly → monthly order (`reduce` holds `a` unless `b` is
  // strictly smaller), so nothing moves for the common case where every window
  // is untouched and the smallest cap wins on its own.
  //
  // COMPARED AT THE TENTH THE LABEL PRINTS, not on the raw float. `used` is
  // rounded to a tenth by `windowState` and `limit` is not, so a raw subtraction
  // drifts: 200 - 180.3 is 19.69999999999999 while 20 - 0.3 is exactly 19.7.
  // Two windows a user reads as "19.7 left" each would then compare unequal and
  // the larger cap would win the tie instead of the daily order this comment
  // promises. Rounding to the printed tenth makes the promise literally true
  // and keeps pick, bar and label agreeing on one number.
  //
  // `usedPct` is deliberately NOT involved: it is a rendering value, pinned to
  // 1..99 below the cap, and it drives the bars only.
  const tokensLeft = (w) => Math.round((w.state.limit - w.state.used) * 10)
  const primary = capped.reduce((a, b) => (tokensLeft(b) < tokensLeft(a) ? b : a))

  // The cap is on the pill now, not only in the card — "3" alone never said
  // whether that was most of the budget or none of it.
  const label = t(`ion.remaining.${primary.key}`, {
    n: fmtAmount(primary.state.used),
    limit: fmtAmount(primary.state.limit),
  })

  // Every window the profile has a FIGURE for, including the unlimited ones.
  // Dropping those silently made the chip lie by omission on the `batemo`
  // profile, whose daily and weekly are unlimited by default (backend
  // quota.py) — it showed one monthly line with nothing to say the other two
  // windows even existed. Per-user overrides can make any window unlimited on
  // any profile, so this is not a batemo-only case.
  //
  // NOT covered: a window whose limit is a literal 0, which the backend uses
  // for "blocked" (quota.py). `windowState` rejects `limit <= 0` and it is not
  // unlimited either, so such a window is invisible here despite being the
  // most binding thing there is. Unreachable for now — `isTieredProfile()`
  // already excludes the profiles that get zeros — but a per-user override of
  // 0 would land in that gap. Left as it was rather than widened here.
  //
  // The binding marker only means something when there is more than one capped
  // window to choose between; on a single-cap profile it is noise.
  const windows = all.map((w) => ({
    key: w.key,
    name: t(`ion.remaining.window.${w.key}`),
    state: w.state,
    binding: capped.length > 1 && w === primary,
  }))

  // The accessible name is ONE sentence — a screen reader reading a table of
  // newlines is worse than a list read in order — and it carries no binding
  // marker: this is an aria-live region re-announced on every turn-end quota
  // tick, and the fact the marker states is already implicit in the numbers.
  const rows = windows.map((w) =>
    w.state
      ? t('ion.remaining.tooltip.row', {
          window: w.name,
          n: fmtAmount(w.state.used),
          limit: fmtAmount(w.state.limit),
        })
      : t('ion.remaining.tooltip.row_unlimited', { window: w.name }),
  )
  const ariaLabel = [t('ion.remaining.tooltip.heading'), rows.join(' · ')].join(' — ')

  // Pointer-only, exactly as the `title` it replaces was: the chip is not a
  // tab stop (making it one would put a live region in the tab order to reveal
  // facts its own aria-label already reads out), so there is no focus branch.
  const track = (e) => setCursor({ x: e.clientX, y: e.clientY })

  return (
    <span
      class={`ion-remaining-chip${primary.state.near ? ' ion-remaining-chip--warn' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      data-testid="ion-remaining-chip"
      onMouseEnter={track}
      onMouseMove={track}
      onMouseLeave={() => setCursor(null)}
    >
      {/* The bar is the glance value; the text beside it is the exact figure.
          Both are decorative to AT — the full sentence is the aria-label. */}
      <span class="ion-remaining-chip__track" aria-hidden="true">
        <span
          class="ion-remaining-chip__fill"
          style={{ width: `${primary.state.usedPct}%` }}
        />
      </span>
      <span class="ion-remaining-chip__label">{label}</span>
      {cursor &&
        typeof document !== 'undefined' &&
        createPortal(<BreakdownCard cursor={cursor} windows={windows} />, document.body)}
    </span>
  )
}
